import { OWNERS } from './mock-data'
import { Persona } from './view-config'

/**
 * Demo persona model. The source data is identical for every viewer; the
 * persona only selects which dashboards and reports the library surfaces, and
 * (for the rep) scopes those reports to a single "logged-in" owner.
 *
 * This is a demo shim, not the eventual product: real role-based dashboards
 * would derive the persona from auth, not a header switcher.
 */

export type { Persona }

/** The "logged-in" sales rep for the demo — a fixed owner from the mock data. */
export const CURRENT_REP = OWNERS[0]

/** Personas in switcher order, with the labels shown in the header control. */
export const PERSONAS: { id: Persona; label: string }[] = [
  { id: 'admin', label: 'Admin' },
  { id: 'rep', label: 'Sales Rep' },
]

export const DEFAULT_PERSONA: Persona = 'admin'

const STORAGE_KEY = 'reporting-prototype.persona'

/** Last-selected persona, so the demo survives a mid-call reload. */
export function loadPersona(): Persona {
  const raw = localStorage.getItem(STORAGE_KEY)
  return raw === 'admin' || raw === 'rep' ? raw : DEFAULT_PERSONA
}

export function savePersona(persona: Persona): void {
  localStorage.setItem(STORAGE_KEY, persona)
}

/**
 * Namespace a saved-library localStorage key by the active persona, so each
 * persona keeps its own "My reports"/"My dashboards"/goals — deleting one
 * persona's saved item doesn't touch the other's (the demo mimics two users).
 * The default persona (admin) keeps the bare base key, so data saved before
 * this split stays where it was — no migration needed.
 */
export function personaStorageKey(baseKey: string): string {
  const persona = loadPersona()
  return persona === DEFAULT_PERSONA ? baseKey : `${baseKey}.${persona}`
}

/**
 * Whether a persona-tagged library item is visible to `persona`. An item
 * tagged `['rep']` is rep-scoped (self-filtered) and hidden from the admin;
 * everything else is team-wide and shown to the admin. The rep sees only the
 * items explicitly tagged for them.
 */
export function visibleForPersona(
  personas: Persona[] | undefined,
  persona: Persona
): boolean {
  return persona === 'rep'
    ? !!personas?.includes('rep')
    : !personas?.includes('rep')
}
